import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { Button } from "@heroui/react";
import SearchFilter from "../../../utils/SearchFilter";
import {
  JODI_REPORT_ENDPOINTS,
  createJodiReportDatasource,
  slNoColumn,
} from "../../../utils/jodiReportServerSide";

ModuleRegistry.registerModules([AllCommunityModule]);

const PAGE_SIZE = 20;

const JodiReportBetsTable = ({ basePath = "/admin" }) => {
  const { matchId, userId, digit } = useParams();
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const searchRef = useRef("");
  const [matchInfo, setMatchInfo] = useState(null);
  const [username, setUsername] = useState("");
  const [gridReady, setGridReady] = useState(false);
  const [quickFilterText, setQuickFilterText] = useState("");

  const columnDefs = useMemo(
    () => [
      slNoColumn,
      { headerName: "Bet ID", field: "bet_id", colId: "bet_id", maxWidth: 100 },
      { headerName: "Digit", field: "number", colId: "number", maxWidth: 100 },
      {
        headerName: "Stake",
        field: "stake",
        colId: "stake",
        valueFormatter: (params) =>
          params.value != null ? Number(params.value).toFixed(2) : "0.00",
      },
      {
        headerName: "Payout",
        field: "payout",
        colId: "payout",
        valueFormatter: (params) =>
          params.value != null ? Number(params.value).toFixed(2) : "0.00",
      },
      // {
      //   headerName: "Session",
      //   field: "session_type",
      //   colId: "session_type",
      //   maxWidth: 100,
      // },
      // {
      //   headerName: "Rate",
      //   field: "rate",
      //   colId: "rate",
      //   maxWidth: 100,
      //   valueFormatter: (params) =>
      //     params.value != null ? Number(params.value).toFixed(2) : "0.00",
      // },
      {
        headerName: "Bet Placed Time",
        field: "bet_time",
        colId: "bet_time",
        flex: 1,
        valueFormatter: (params) =>
          params.value ? new Date(params.value).toLocaleString() : "—",
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
    }),
    []
  );

  const datasource = useMemo(
    () =>
      createJodiReportDatasource({
        endpoint: JODI_REPORT_ENDPOINTS.bets,
        getBasePayload: () => ({
          matchId: parseInt(matchId, 10),
          userId,
          digit,
        }),
        getSearch: () => searchRef.current,
        onMeta: (data) => {
          if (data?.match) setMatchInfo(data.match);
          if (data?.username) setUsername(data.username);
        },
      }),
    [matchId, userId, digit]
  );

  const onGridReady = useCallback(
    (params) => {
      gridRef.current = params.api;
      params.api.setGridOption("datasource", datasource);
      setGridReady(true);
    },
    [datasource]
  );

  useEffect(() => {
    searchRef.current = quickFilterText;
    if (!gridReady || !gridRef.current) return;

    const timer = setTimeout(() => {
      gridRef.current.refreshInfiniteCache();
    }, 300);

    return () => clearTimeout(timer);
  }, [quickFilterText, gridReady]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Jodi Report — All Bets
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            User: {username || userId} | Digit: {digit}
          </p>
          {matchInfo && (
            <p className="text-sm text-gray-600">
              {matchInfo.market_name} — {matchInfo.name}
            </p>
          )}
        </div>
        <Button
          color="default"
          variant="flat"
          onPress={() =>
            navigate(`${basePath}/jodi-report-users/${matchId}/${digit}`)
          }
        >
          Back to Users
        </Button>
      </div>

      <SearchFilter
        value={quickFilterText}
        onChange={(e) => setQuickFilterText(e.target.value)}
      />

      <div className="ag-theme-alpine" style={{ height: 700, width: "100%" }}>
        <AgGridReact
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowModelType="infinite"
          cacheBlockSize={PAGE_SIZE}
          maxBlocksInCache={5}
          pagination={true}
          paginationPageSize={PAGE_SIZE}
          onGridReady={onGridReady}
          onSortChanged={(params) => params.api.refreshInfiniteCache()}
          enableCellTextSelection={true}
          overlayNoRowsTemplate="<span style='padding: 10px; display: block;'>No bets found</span>"
        />
      </div>
    </div>
  );
};

export default JodiReportBetsTable;
